import { GameState, Player } from '../../../domain/entities';
import { getPlayerTeam, addNewsItem } from '../../GameService';

/**
 * InjuryProcessor - Handles player injuries after matches
 *
 * Responsibilities:
 * - Calculate injury risk based on fitness, age
 * - Determine injury severity
 * - Apply injuries to players
 * - Generate injury news items
 * - Process injury recovery
 */
export class InjuryProcessor {
    /**
     * Process injuries for players who participated in matches
     */
    static processInjuries(
        gameState: GameState,
        playersWhoPlayed: Set<number>,
        allPlayersById: Map<number, Player>
    ): void {
        playersWhoPlayed.forEach(id => {
            const player = allPlayersById.get(id);
            if (player && !player.injury) {
                const injuryChance = this.calculateInjuryChance(player);

                if (Math.random() < injuryChance) {
                    const severeInjuryChance = this.calculateSevereInjuryChance(player);
                    const isSevere = Math.random() < severeInjuryChance;

                    const injuryType = isSevere ? 'Severe' : 'Light';
                    const injuryDuration = isSevere
                        ? 4 + Math.floor(Math.random() * 20)  // 4-23 weeks
                        : 1 + Math.floor(Math.random() * 3);   // 1-3 weeks

                    player.injury = {
                        type: injuryType,
                        duration: injuryDuration
                    };

                    // Add to injury history
                    if (!player.injuryHistory) {
                        player.injuryHistory = [];
                    }
                    player.injuryHistory.push({
                        season: gameState.season,
                        week: gameState.currentWeek,
                        type: injuryType,
                        duration: injuryDuration
                    });

                    // Notify if player team
                    if (getPlayerTeam(gameState).squad.some(p => p.id === player.id)) {
                        addNewsItem(
                            gameState,
                            'Player Injured',
                            `${player.name} picked up a ${player.injury.type.toLowerCase()} injury and will be out for an estimated ${player.injury.duration} weeks.`,
                            'injury',
                            undefined,
                            { type: 'playerDetail', title: `Player: ${player.name}`, playerId: player.id }
                        );
                    }
                }
            }
        });
    }

    /**
     * Process injury recovery for all injured players
     */
    static processRecovery(gameState: GameState, allPlayers: Player[]): void {
        allPlayers.forEach(player => {
            if (player.injury) {
                player.injury.duration--;

                if (player.injury.duration <= 0) {
                    // Notify if player team
                    if (getPlayerTeam(gameState).squad.some(p => p.id === player.id)) {
                        addNewsItem(
                            gameState,
                            'Player Recovers',
                            `${player.name} has recovered from their injury and is available for selection.`,
                            'recovery',
                            undefined,
                            { type: 'playerDetail', title: `Player: ${player.name}`, playerId: player.id }
                        );
                    }
                    player.injury = null;
                }
            }
        });
    }

    /**
     * Calculate base injury chance for a player
     * Factors: fitness, age
     */
    private static calculateInjuryChance(player: Player): number {
        let injuryChance = 0.02; // Base 2% chance

        // Low fitness increases injury risk
        if (player.fitness < 50) {
            injuryChance += (50 - player.fitness) * 0.002;
            // At 10 fitness: 0.02 + 0.08 = 0.1 (10%)
        }

        // Age over 30 increases injury risk
        if (player.age > 30) {
            injuryChance += (player.age - 30) * 0.005;
            // At age 35: 0.02 + 0.025 = 0.045 (4.5% base)
        }

        return injuryChance;
    }

    /**
     * Calculate probability of severe injury vs light injury
     * Dramatically increases at very low fitness
     */
    private static calculateSevereInjuryChance(player: Player): number {
        // Base 10% chance, but at 0 fitness it's 70% chance of severe injury
        return 0.1 + Math.max(0, (30 - player.fitness) * 0.02);
    }
}
